import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, collection, query, where, getDocs, updateDoc, serverTimestamp } from "firebase/firestore";
import TaskChat from "../components/TaskChat";
import { useAuth } from "../context/AuthContext";
import { FaArrowLeft, FaCalendarAlt, FaUser, FaTag, FaEdit } from "react-icons/fa";
import { FiCheckSquare } from "react-icons/fi";
import EditTaskModal from "../components/EditTaskModal";
import "react-quill-new/dist/quill.snow.css";
import "./Tasks.css";

const TaskTicket = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  useEffect(() => {
    const fetchTask = async () => {
      try {
        const q = query(collection(db, "tasks"), where("ticketNumber", "==", taskId));
        const snap = await getDocs(q);
        if (!snap.empty) {
          setTask({ id: snap.docs[0].id, ...snap.docs[0].data() });
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchTask();
  }, [taskId]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateDoc(doc(db, "tasks", task.id), { 
        status: newStatus,
        completedAt: newStatus === 'Completed' ? serverTimestamp() : null
      });
      setTask(prev => ({
        ...prev,
        status: newStatus,
        completedAt: newStatus === 'Completed' ? new Date() : null
      }));
      showNotification(`Status updated to ${newStatus}`);
    } catch (err) {
      showNotification("Failed to update status.", "error");
    }
  };

  if (loading) return (
    <div className="ticket-loading-state">
      <FiCheckSquare size={36} />
      <p>Loading ticket…</p>
    </div>
  );
  if (!task) return (
    <div className="ticket-error-state">
      <h4>Ticket not found</h4>
      <p>This ticket may have been deleted or the ID is incorrect.</p>
    </div>
  );

  return (
    <div className="jira-ticket-page">
      {notification.show && (
        <div className={`tasks-notification tasks-notification--${notification.type}`}>
          {notification.message}
        </div>
      )}
      <button className="back-btn" onClick={() => navigate(-1)}><FaArrowLeft /> Back to Board</button>

      <div className="jira-header">
        <div className="jira-title-block">
          <div className="jira-ticket-id-row">
            <span className="jira-ticket-id-label">Ticket ID:</span>
            <span className="jira-ticket-id">{task.ticketNumber}</span>
          </div>
          <h2 className="jira-title">{task.title}</h2>
        </div>
        <div className="jira-actions">
          {(userData.role === 'admin' || userData.role === 'superadmin') && (
            <button className="jira-btn" onClick={() => setEditing(true)}>
              <FaEdit size={12} /> Edit Details
            </button>
          )}
          <select
            className={`jira-status-dropdown jira-status--${task.status.toLowerCase().replace(/\s/g, '-')}`}
            value={task.status}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            {['Pending', 'In Progress', 'On Hold', 'Completed'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="jira-main-content">
        <div className="jira-left">
          <div className="jira-section">
            <h4><FaTag /> Details</h4>
            <div className="jira-details-inline">
              <span><b>Priority:</b>{' '}
                <span className={`tasks-prio-badge tasks-prio--${(task.priority || 'normal').toLowerCase()}`}>
                  {task.priority || 'Normal'}
                </span>
              </span>
              <span><b>Status:</b>{' '}
                <span className={`tasks-status-badge tasks-status--${task.status.toLowerCase().replace(/\s/g, '-')}`}>
                  {task.status}
                </span>
              </span>
              <span className="jira-labels-group"><b>Labels:</b>{' '}
                {task.labels
                  ? task.labels.split(',').map(l => l.trim()).filter(Boolean).map((l, i) => (
                      <span key={i} className="jira-label-chip">{l}</span>
                    ))
                  : <span className="jira-no-label">None</span>}
              </span>
            </div>
          </div>
          <div className="jira-section">
            <h4>Description</h4>
            {task.description && task.description.trim() !== '' && task.description.trim() !== '<p><br></p>' ? (
              <div
                className="jira-description"
                dangerouslySetInnerHTML={{ __html: task.description }}
              />
            ) : (
              <div className="jira-description jira-description--empty">No technical notes provided.</div>
            )}
          </div>
          <div className="jira-section">
            <h4>Activity & Discussion</h4>
            <div className="jira-comments-block">
              <TaskChat taskId={task.id} user={userData} jiraStyle />
            </div>
          </div>
        </div>
        <div className="jira-right">
          <div className="jira-section">
            <h4><FaUser /> People</h4>
            <div className="people-list">
              <p><b>Reporter:</b> {task.assignedByName || 'Admin'}</p>
              <p><b>Assignee:</b> {
                task.uid === userData.uid
                  ? "You"
                  : (task.assignedToName || task.name || task.email || "Employee")
              }</p>
            </div>
          </div>
          <div className="jira-section">
            <h4><FaCalendarAlt /> Dates</h4>
            <div className="date-list">
              <p><b>Created:</b>{' '}
                {task.assignedAt?.toDate
                  ? task.assignedAt.toDate().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })
                  : (task.assignedAt ? new Date(task.assignedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A')}
              </p>
              <p><b>Due Date:</b>{' '}
                <span className={`tasks-due-pill${task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'Completed' ? ' tasks-due-pill--overdue' : ''}`}>
                  {task.dueDate || '—'}
                </span>
              </p>
              {task.status === 'Completed' && task.completedAt && (
                <p><b>Completed:</b>{' '}
                  {task.completedAt?.toDate
                    ? task.completedAt.toDate().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })
                    : new Date(task.completedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {editing && (
        <EditTaskModal 
          task={task} 
          onClose={() => setEditing(false)} 
          onSave={async (updated) => {
            await updateDoc(doc(db, "tasks", task.id), updated);
            setTask({...task, ...updated});
            setEditing(false);
          }}
        />
      )}
    </div>
  );
};

export default TaskTicket;